import React from 'react';
import { Award, ChefHat, Clock, Heart, Leaf, ShoppingCart, Users, Wine } from 'lucide-react';

const Benefits = ({ language }) => {
  const texts = {
    en: {
      title: 'Why Choose Souschaif?',
      subtitle: 'A kitchen revolution at your fingertips',
      benefits: [
        {
          icon: Leaf,
          title: 'Reduce Food Waste and Save Money',
          description: 'Fight food waste at home by using every last bit of your groceries in practical, delicious recipes.'
        },
        {
          icon: Award,
          title: 'Food Waste Calculator',
          description: 'Track how much you are saving, both environmentally and financially, with every meal you make.'
        },
        {
          icon: Wine,
          title: 'Perfect Wine Pairings',
          description: 'Discover curated wine pairing ideas to complement cozy dinners, special meals, and nights with friends.'
        },
        {
          icon: Users,
          title: 'Community of Home Cooks',
          description: 'Share recipes, discover new meal ideas, and join a movement toward more sustainable cooking.'
        },
        {
          icon: ShoppingCart,
          title: 'Convenience Meets Personalization',
          description: 'Spend less time stressing over what to cook and more time enjoying home-cooked meals.'
        },
        {
          icon: ChefHat,
          title: 'Unleash Your Inner Chef',
          description: 'Explore cuisines from around the world and discover new ways to cook your favorite ingredients.'
        }
      ]
    },
    sl: {
      title: 'Zakaj izbrati Souschaif?',
      subtitle: 'Kuhinjska revolucija na dosegu roke',
      benefits: [
        {
          icon: Leaf,
          title: 'Manj odpadne hrane in več prihranka',
          description: 'Zmanjšajte odpadno hrano doma tako, da uporabite čim več živil v praktičnih in okusnih receptih.'
        },
        {
          icon: Award,
          title: 'Kalkulator odpadne hrane',
          description: 'Spremljajte, koliko prihranite za okolje in denarnico z vsakim pripravljenim obrokom.'
        },
        {
          icon: Wine,
          title: 'Popolne vinske kombinacije',
          description: 'Odkrijte ideje za vinske kombinacije za domače večerje, posebne obroke in druženja s prijatelji.'
        },
        {
          icon: Users,
          title: 'Skupnost domačih kuharjev',
          description: 'Delite recepte, odkrivajte nove ideje za obroke in se pridružite bolj trajnostnemu kuhanju.'
        },
        {
          icon: ShoppingCart,
          title: 'Udobje in personalizacija',
          description: 'Manj časa porabite za razmišljanje, kaj kuhati, in več časa za uživanje v domačih obrokih.'
        },
        {
          icon: ChefHat,
          title: 'Prebudite notranjega chefa',
          description: 'Raziskujte svetovne kuhinje in odkrijte nove načine za pripravo najljubših sestavin.'
        }
      ]
    }
  };

  const t = texts[language];

  return (
    <section id="section-4" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.benefits.map((benefit, index) => (
            <div
              key={index}
              className="group bg-gradient-to-br from-emerald-50 to-lime-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <benefit.icon className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {benefit.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* Souschaif lifestyle image */}
        <div className="mt-20 relative">
          <div className="relative max-w-5xl mx-auto">
            {/* Background decoration */}
            <div className="absolute -inset-8 bg-gradient-to-br from-emerald-100 via-lime-100 to-green-100 rounded-3xl opacity-50"></div>
            
            <div className="relative bg-white rounded-3xl p-8 shadow-2xl">
              <div className="grid md:grid-cols-5 gap-6 items-center mb-6">
                <div className="md:col-span-2 space-y-3">
                  <div className="inline-flex items-center space-x-2 bg-emerald-600 text-white rounded-full px-4 py-2">
                    <Heart className="w-5 h-5" />
                    <span className="text-sm font-semibold">
                      {language === 'en' ? 'Sustainability Made Simple' : 'Trajnost na preprost način'}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {language === 'en' ? 'Cook With What You Have' : 'Kuhajte s tem, kar imate'}
                  </h3>
                  <p className="text-gray-600">
                    {language === 'en' 
                      ? 'Souschaif makes sustainable cooking simple by helping you minimize unnecessary waste while keeping meals creative.'
                      : 'Souschaif poenostavi trajnostno kuhanje, pomaga zmanjšati nepotrebne odpadke in ohranja obroke ustvarjalne.'}
                  </p>
                </div>
                
                <div className="md:col-span-3 relative">
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-emerald-600 rounded-full opacity-10 blur-2xl"></div>
                  <img
                    src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=1200&q=80"
                    alt={language === 'en' ? 'Colorful healthy meal bowl with fresh vegetables' : 'Barvita zdrava skleda s svežo zelenjavo'}
                    className="w-full h-[340px] object-cover rounded-2xl shadow-xl transform hover:scale-[1.02] transition-transform duration-500"
                  />
                  
                  {/* Floating stats */}
                  <div className="absolute -bottom-4 -left-4 bg-white rounded-xl px-4 py-3 shadow-xl">
                    <div className="text-2xl font-bold text-emerald-600">
                      <Clock className="w-6 h-6 inline-block mr-1" />
                      24/7
                    </div>
                    <div className="text-xs text-gray-600">
                      {language === 'en' ? 'Meal inspiration' : 'Navdih za obroke'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;
